// "Import stores" (Stores › Import): the dialog's words, picker options and the save loop, apart from React so they
// are unit-tested. Port of CSVImportExportView / CSVImportSheet (GroundTeam/Views/Settings/IntegrationsView.swift);
// parsing, duplicate rules and payloads are in domain/importRecords.ts.
//
// The save loop writes `IMPORT_CHUNK_SIZE` rows per `engine.save` (each batch atomic: a store and its "created"
// activity are stored together or not at all), reports progress after each batch, yields so the page can paint, and
// checks for Stop between batches. A refused batch (SyncNotReadyError, an invalid payload) ends the loop; the batches
// before it stay saved and are pushed as usual.
//
// The owner is always a person (the importer by default, as iOS `addRecord`): an unowned store is on every rep's phone
// (iOS `visibleRecords` and the server's `record_visible_to` show `ownerId == nil` to everyone), which is the opposite of
// "import now, hand out later". Unassigned stays available on the map's Assign.

import { RECORD_TYPES, RECORD_TYPE_TITLES, type RecordType } from '@/contracts/enums';
import type { LeadSourcePayload, UserPayload } from '@/contracts/models';
import { UNASSIGNED_OWNER_VALUE, ownerPickerOptions } from '@/domain/assign';
import { formatGrouped } from '@/domain/format';
import {
  IMPORT_FIELDS,
  IMPORT_HEADER_ALIASES,
  importChunks,
  type ImportColumns,
  type ImportPlan,
  type ImportRow,
  type ParsedImport,
} from '@/domain/importRecords';
import { compareLocalized, sameId } from '@/domain/util';
import type { Patch } from '@/sync/patch';

/** Rows per `engine.save` (about 500 outbox entries with the "created" activities). */
export const IMPORT_CHUNK_SIZE = 250;
/** Rows in the preview table. */
export const PREVIEW_ROW_LIMIT = 50;

// MARK: - Words

export const IMPORT_TITLE = 'Import stores';
export const IMPORT_DESCRIPTION =
  'Add stores from a CSV file. They are saved to your company and show up on the map, where you can assign them to your team.';
/** The empty Map and Stores list for people who can import (next to an "Import stores" button). */
export const NO_STORES_IMPORT_TEXT = 'Stores your team adds in the Ground Team app will show up here. You can also import them from a CSV file.';
export const IMPORT_COLUMNS_HINT =
  'Recognized columns (any order, case-insensitive): name, company, street/address, city, state, zip, latitude, longitude, phone, email, website, type, priority, value, notes, tags, lead source, external id. The first line must be the header.';
/** CSVImportExportView's error for a file without rows. */
export const NO_ROWS_TEXT = 'No rows were found. Make sure the first line is a header with at least a name or company column.';
export const READ_ERROR_TEXT = "Couldn't read this file. Choose a CSV file saved as text (UTF-8).";
export const NOTHING_TO_ADD_TEXT = 'Every row is already in Ground Team, repeated in the file or has no name, so there is nothing to add.';
/** Every row was skipped for having no name, company or street (the columns exist but are empty). */
export const NO_NAMES_TEXT = 'None of the rows has a name, company or street, so there is nothing to add.';
/** The other header names the name and company columns may have (the list stays in step with the aliases). */
const OTHER_NAME_HEADERS = [...IMPORT_HEADER_ALIASES.name, ...IMPORT_HEADER_ALIASES.company].filter((h) => h !== 'name').join(', ');
/** No name or company column, and no street to fall back on: every row would be skipped. */
export const NO_NAME_COLUMN_TEXT = `No name or company column was found. Rename the column with the store names to “name” (also recognized: ${OTHER_NAME_HEADERS}) and choose the file again.`;
/** No name or company column, but a street one: each store would be named after its street (iOS does the same). */
export const STREET_NAMES_TEXT =
  'No name or company column was found, so each store would be named after its street address. If the file has store names, rename that column to “name” and choose the file again.';
/** The checkbox that must be ticked before importing stores named by street. */
export const STREET_NAMES_CONFIRM = 'Name the stores by street address';
export const OWNER_PLACEHOLDER = 'Choose an owner…';
export const OWNER_HINT = 'Reps see the stores they own in the Ground Team app. You can reassign stores from the map at any time.';
/** Added for admins: managers see only stores owned by people they can see (iOS `visibleRecords`). */
export const ADMIN_OWNER_NOTE = 'Managers see only stores owned by their team.';
export const SOURCE_HINT = 'A lead source column in the file wins for its rows.';
export const TYPE_HINT = 'As in the file: the type column, else Account for rows with only a company, else Lead.';
export const SAVE_FAILED_TEXT = "Some stores couldn't be saved, so the import stopped.";
/** After the toast's counts: saved stores reach the server (and reps' phones) as the outbox pushes. */
export const UPLOAD_NOTE = 'They upload in the background while this tab is open.';

/** Select value for "As in the file" (no type override). */
export const TYPE_FROM_FILE_VALUE = 'file';

function plural(n: number, one: string, many: string): string {
  return `${formatGrouped(n)} ${n === 1 ? one : many}`;
}

/** The confirm button: "Import 4,734 stores". */
export function importButtonLabel(count: number): string {
  return `Import ${plural(count, 'store', 'stores')}`;
}

/** Progress line: "Imported 1,250 of 4,734". */
export function importProgressText(done: number, total: number): string {
  return `Imported ${formatGrouped(done)} of ${formatGrouped(total)}`;
}

/** The warning under the summary when some stores have no coordinates (null when all have). */
export function noCoordinatesText(count: number): string | null {
  if (count === 0) return null;
  return count === 1
    ? "1 store has no latitude/longitude. It is imported but won't show on the map until its location is set."
    : `${formatGrouped(count)} stores have no latitude/longitude. They are imported but won't show on the map until their location is set.`;
}

/** Note for lead source names in the file that the company does not have (null when none). */
export function unknownSourcesText(names: readonly string[], defaultSourceName: string | null): string | null {
  if (names.length === 0) return null;
  const shown = names.slice(0, 3).map((n) => `“${n}”`).join(', ') + (names.length > 3 ? ` and ${formatGrouped(names.length - 3)} more` : '');
  const fallback = defaultSourceName ? `get ${defaultSourceName}` : 'get no lead source';
  return `Lead sources not in your settings: ${shown}. Those rows ${fallback}.`;
}

/** Summary lines of the review step, in order: [label, count]. Zero "skipped" lines are left out. */
export function planSummary(plan: ImportPlan): { label: string; value: number; tone?: 'muted' | 'warning' }[] {
  const out: { label: string; value: number; tone?: 'muted' | 'warning' }[] = [
    { label: 'Rows in file', value: plan.total },
    { label: 'Will be added', value: plan.toAdd.length },
  ];
  if (plan.skippedExisting > 0) out.push({ label: 'Already in Ground Team (skipped)', value: plan.skippedExisting, tone: 'muted' });
  if (plan.skippedRepeated > 0) out.push({ label: 'Repeated in the file (skipped)', value: plan.skippedRepeated, tone: 'muted' });
  if (plan.skippedNoName > 0) out.push({ label: 'No name (skipped)', value: plan.skippedNoName, tone: 'muted' });
  if (plan.withoutCoordinates > 0) out.push({ label: 'No coordinates (not on the map)', value: plan.withoutCoordinates, tone: 'warning' });
  return out;
}

/**
 * What the header lacks for naming stores: null when there is a name or company column; 'street' when there is
 * neither but a street column (stores would be named by street: the dialog asks first); 'none' when there is none of
 * the three (every row would be skipped: the dialog says which column to add).
 */
export function nameColumnProblem(columns: ImportColumns): 'street' | 'none' | null {
  if (columns.name !== undefined || columns.company !== undefined) return null;
  return columns.street !== undefined ? 'street' : 'none';
}

/** The line under the summary when nothing can be added (null when something can). */
export function nothingToAddText(plan: Pick<ImportPlan, 'total' | 'toAdd' | 'skippedNoName'>, columns: ImportColumns): string | null {
  if (plan.toAdd.length > 0) return null;
  if (nameColumnProblem(columns) === 'none') return NO_NAME_COLUMN_TEXT;
  if (plan.total > 0 && plan.skippedNoName === plan.total) return NO_NAMES_TEXT;
  return NOTHING_TO_ADD_TEXT;
}

/** Header cells the import uses and the ones it ignores, for "Columns used" / "Ignored". */
export function columnUsage(parsed: Pick<ParsedImport, 'header' | 'columns'>): { used: string[]; ignored: string[] } {
  const usedIdx = new Set<number>();
  for (const f of IMPORT_FIELDS) {
    const i = parsed.columns[f];
    if (i !== undefined) usedIdx.add(i);
  }
  const used: string[] = [];
  const ignored: string[] = [];
  parsed.header.forEach((h, i) => {
    if (h === '') return;
    (usedIdx.has(i) ? used : ignored).push(h);
  });
  return { used, ignored };
}

// MARK: - Options

export interface Option {
  value: string;
  label: string;
}

/** Owner picker: the map Assign's list ("Name · Role", "(you)" for the viewer) without "Unassigned" (file comment). */
export function ownerOptions(people: readonly UserPayload[], meId: string | null | undefined): Option[] {
  return ownerPickerOptions(people, meId, { unassigned: false });
}

/** The owner picked when the dialog opens: the viewer when they are in the list, else none ('': Import waits for a choice). */
export function defaultOwnerValue(people: readonly UserPayload[], meId: string | null | undefined): string {
  const me = people.find((u) => sameId(u.id, meId));
  return me ? me.id : '';
}

/** The picked owner as a payload value (null: none chosen). */
export function ownerIdFromValue(value: string): string | null {
  return value === UNASSIGNED_OWNER_VALUE || value === '' ? null : value;
}

/** The Owner field's hint (admins also learn that managers only see their team's stores). */
export function ownerHint(viewerIsAdmin: boolean): string {
  return viewerIsAdmin ? `${OWNER_HINT} ${ADMIN_OWNER_NOTE}` : OWNER_HINT;
}

/** Lead source picker: "None", then the company's sources by name. */
export function sourceOptions(sources: readonly LeadSourcePayload[]): Option[] {
  const sorted = [...sources].sort((a, b) => compareLocalized(a.name, b.name));
  return [{ value: '', label: 'None' }, ...sorted.map((s) => ({ value: s.id, label: s.name || 'Unnamed source' }))];
}

/** Record type picker: "As in the file", then each type. */
export function typeOptions(): Option[] {
  return [{ value: TYPE_FROM_FILE_VALUE, label: 'As in the file' }, ...RECORD_TYPES.map((t) => ({ value: t, label: RECORD_TYPE_TITLES[t] }))];
}

export function typeOverrideFromValue(value: string): RecordType | null {
  return (RECORD_TYPES as readonly string[]).includes(value) ? (value as RecordType) : null;
}

// MARK: - Reading the file

/**
 * The file's bytes as text. Strict UTF-8 (a leading BOM is dropped): a file saved in another encoding (Excel's
 * "CSV (Comma delimited)" on Windows is Windows-1252) throws instead of importing "Caf�" into thousands of stores, so
 * the dialog shows READ_ERROR_TEXT.
 */
export function decodeImportText(bytes: ArrayBuffer | Uint8Array): string {
  return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
}

// MARK: - Preview

export interface PreviewRow {
  id: string;
  name: string;
  city: string;
  state: string;
  phone: string;
  tags: string;
}

/** The first rows that will be added, as the preview table shows them. */
export function previewRows(rows: readonly ImportRow[], limit = PREVIEW_ROW_LIMIT): PreviewRow[] {
  return rows.slice(0, limit).map((r) => ({
    id: String(r.rowNumber),
    name: r.name !== '' ? r.name : r.street,
    city: r.city,
    state: r.state,
    phone: r.phone,
    tags: r.tags.join(', '),
  }));
}

// MARK: - Saving

export interface ImportOutcome {
  /** Stores the import set out to add. */
  total: number;
  /** Stores saved (whole batches). */
  imported: number;
  /** Stop was pressed (or the dialog went away) before every batch was saved. */
  stopped: boolean;
  /** Why a batch was refused (nothing of that batch was saved), or null. */
  error: unknown;
}

export interface ImportRunDeps {
  /** `engine.save` (throws SyncNotReadyError or on an invalid patch; nothing of that call is stored then). */
  save: (patches: readonly Patch[]) => unknown;
  /** After each saved batch. */
  onProgress?: (imported: number, total: number) => void;
  /** Checked before each batch. */
  shouldStop?: () => boolean;
  /** Between batches, so the progress bar paints (default: a zero-delay timer). */
  pause?: () => Promise<void>;
  chunkSize?: number;
}

const nextTask = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

/** Saves the rows batch by batch (see the file comment). Never throws: a refused batch ends it with `error`. */
export async function runImport(
  rows: readonly ImportRow[],
  build: (rows: readonly ImportRow[]) => Patch[],
  deps: ImportRunDeps,
): Promise<ImportOutcome> {
  const total = rows.length;
  let imported = 0;
  const batches = importChunks(rows, deps.chunkSize ?? IMPORT_CHUNK_SIZE);
  for (const batch of batches) {
    if (deps.shouldStop?.()) return { total, imported, stopped: true, error: null };
    try {
      deps.save(build(batch));
    } catch (error) {
      return { total, imported, stopped: false, error };
    }
    imported += batch.length;
    deps.onProgress?.(imported, total);
    if (imported < total) await (deps.pause ?? nextTask)();
  }
  return { total, imported, stopped: false, error: null };
}

/** Error text for a refused batch, in plain words (SyncNotReadyError keeps its own). */
export function importErrorMessage(error: unknown): string {
  if (error instanceof Error && error.name === 'SyncNotReadyError') return error.message;
  return SAVE_FAILED_TEXT;
}

/** The toast after an import: tone and words. */
export function importResultToast(outcome: ImportOutcome, plan: Pick<ImportPlan, 'total' | 'toAdd' | 'withoutCoordinates'>): {
  tone: 'success' | 'info' | 'error';
  message: string;
} {
  const { imported, total } = outcome;
  if (outcome.error !== null && outcome.error !== undefined) {
    const done = imported > 0 ? ` ${importProgressText(imported, total)} stores.` : ' No stores were imported.';
    return { tone: 'error', message: `${importErrorMessage(outcome.error)}${done}` };
  }
  if (outcome.stopped) {
    const note = imported > 0 ? ` ${UPLOAD_NOTE}` : '';
    return { tone: 'info', message: `Import stopped. ${importProgressText(imported, total)} stores.${note}` };
  }
  const skipped = plan.total - plan.toAdd.length;
  let message = `Imported ${plural(imported, 'store', 'stores')}`;
  if (skipped > 0) message += ` · ${formatGrouped(skipped)} skipped`;
  if (plan.withoutCoordinates > 0) message += ` · ${formatGrouped(plan.withoutCoordinates)} without coordinates`;
  return { tone: 'success', message: imported > 0 ? `${message}. ${UPLOAD_NOTE}` : `${message}.` };
}
